import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api/client";

export default function AdminUserSearches() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [searches, setSearches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/users/${userId}`),
      api.get(`/admin/searches?user_id=${userId}&page_size=200`),
    ]).then(([userRes, searchesRes]: any[]) => {
      setUser(userRes.data);
      setSearches(searchesRes.data.items);
      setLoading(false);
    });
  }, [userId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE": return "#10b981";
      case "PAUSED": return "#f59e0b";
      case "EXPIRED": return "#9ca3af";
      default: return "#ef4444";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "ACTIVE": return "Активен";
      case "PAUSED": return "На паузе";
      case "EXPIRED": return "Истёк";
      default: return status;
    }
  };

  const formatFilters = (f: any) => {
    if (!f) return "Без фильтров";
    const parts: string[] = [];
    if (f.price_min || f.price_max) {
      parts.push(`Цена: ${f.price_min || 0}\u2013${f.price_max || "\u221e"} \u20bd`);
    }
    if (f.seller_type && f.seller_type !== "ANY") {
      parts.push(f.seller_type === "PRIVATE" ? "Только частные" : "Только магазины");
    }
    if (f.hide_frequent_sellers) parts.push("Скрывать частых продавцов");
    if (f.price_drop_alerts) parts.push("Снижение цены");
    if (f.include_keywords?.length) parts.push(`Включить: ${f.include_keywords.join(", ")}`);
    if (f.exclude_keywords?.length) parts.push(`Исключить: ${f.exclude_keywords.join(", ")}`);
    return parts.length ? parts.join(" \u2022 ") : "Без фильтров";
  };

  const active = searches.filter((s) => !s.is_deleted && s.status === "ACTIVE");
  const paused = searches.filter((s) => !s.is_deleted && s.status === "PAUSED");
  const other = searches.filter((s) => !s.is_deleted && s.status !== "ACTIVE" && s.status !== "PAUSED");
  const deleted = searches.filter((s) => s.is_deleted);

  // 2026-10-29: the admin pauses / starts a search here
  const setStatus = async (e: any, s: any, status: string) => {
    e.preventDefault();
    e.stopPropagation();
    const what = status === "PAUSED" ? "Поставить на паузу" : "Запустить";
    if (!window.confirm(`${what} «${s.name}»? Клиенту ничего не придёт — при желании напишите ему.`)) return;
    try {
      const r: any = await api.post(`/admin/searches/${s.id}/status`, { status });
      setSearches((list) => list.map((x) => (x.id === s.id ? { ...x, status: r.data.status } : x)));
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || "Не получилось");
    }
  };

  const renderGroup = (title: string, items: any[], emoji: string) => {
    if (items.length === 0) return null;
    return (
      <div style={styles.group}>
        <div style={styles.groupTitle}>{emoji} {title} ({items.length})</div>
        <div style={styles.list}>
          {items.map((s) => (
            <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" style={styles.card}>
              <div style={styles.cardTop}>
                <span style={styles.searchName}>{s.name}</span>
                <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(s.status) }}>
                  {getStatusText(s.status)}
                </span>
              </div>
              <div style={styles.hint}>
                {s.source.toUpperCase()} | Проверено: {s.last_checked_at ? new Date(s.last_checked_at).toLocaleString("ru-RU") : "\u2014"}
              </div>
              <div style={styles.filtersLine}>{formatFilters(s.filters)}</div>
              {!s.is_deleted && (s.status === "ACTIVE" || s.status === "PAUSED") && (
                <button style={styles.pauseBtn} onClick={(e) => setStatus(e, s, s.status === "ACTIVE" ? "PAUSED" : "ACTIVE")}>
                  {s.status === "ACTIVE" ? "⏸ Пауза" : "▶️ Запустить"}
                </button>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => navigate("/admin/users")}>
        ← К пользователям
      </button>

      {loading ? (
        <div style={styles.center}><div style={styles.spinner} /></div>
      ) : (
  
      <>
          {user && (
            <div style={styles.userCard}>
              <div style={styles.userName}>
                {user.first_name || ""} {user.last_name || ""} (@{user.username || "\u2014"})
              </div>
              <div style={styles.userMeta}>Telegram ID: {user.telegram_id}</div>
              <div style={styles.userMeta}>
                Регистрация: {new Date(user.created_at).toLocaleString("ru-RU")}
              </div>
              <div style={styles.userMeta}>
                Последний визит: {user.last_seen_at ? new Date(user.last_seen_at).toLocaleString("ru-RU") : "\u2014"}
              </div>
              <div style={styles.userMeta}>
                Подписка: {user.active_subscription_plan || (user.trial_used ? "Пробный период использован" : "Нет")}
              </div>
              {user.is_blocked && <div style={styles.blockedBadge}>Заблокирован</div>}
            </div>
          )}

          <h1 style={styles.title}>Поиски пользователя</h1>

          {searches.length === 0 ? (
            <div style={styles.hint}>У этого пользователя нет поисков</div>
          ) : (
            <>
              {renderGroup("Активные", active, "\ud83d\udfe2")}
              {renderGroup("На паузе", paused, "\u23f8")}
              {renderGroup("Истёкшие / другие", other, "\u26aa")}
              {renderGroup("Удалённые", deleted, "\ud83d\uddd1")}
            </>
          )}
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  backBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  center: { display: "flex", justifyContent: "center", padding: "40px 0" },
  spinner: { width: "28px", height: "28px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
  userCard: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "14px", padding: "14px", marginBottom: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  userName: { fontSize: "16px", fontWeight: 700, color: "var(--tg-theme-text-color, #000)", marginBottom: "6px" },
  userMeta: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginBottom: "2px" },
  blockedBadge: { display: "inline-block", marginTop: "6px", padding: "2px 8px", borderRadius: "10px", backgroundColor: "#ef4444", color: "#fff", fontSize: "11px" },
  title: { fontSize: "18px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  hint: { fontSize: "13px", color: "var(--tg-theme-hint-color, #666)" },
  group: { marginBottom: "18px" },
  groupTitle: { fontSize: "13px", fontWeight: 600, color: "var(--tg-theme-hint-color, #666)", marginBottom: "8px", textTransform: "uppercase" },
  list: { display: "flex", flexDirection: "column", gap: "10px" },
  card: { display: "block", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "14px", padding: "12px 14px", textDecoration: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" },
  searchName: { fontSize: "14px", fontWeight: 600, color: "var(--tg-theme-text-color, #000)" },
  statusBadge: { fontSize: "11px", color: "#fff", padding: "2px 8px", borderRadius: "10px" },
  pauseBtn: { marginTop: "8px", padding: "6px 12px", borderRadius: "8px", border: "1px solid rgba(127,127,127,0.35)", background: "transparent", color: "var(--tg-theme-text-color, #000)", fontSize: "13px", cursor: "pointer" },
  filtersLine: { fontSize: "11px", color: "var(--tg-theme-hint-color, #888)", marginTop: "4px" },
};
